import { useEffect, useState } from "react";

const INK = "#0d1440";
const EMPTY_MEMBER = { name: "", relationship: "", phone: "", email: "", allow_reports: false, allow_evolution: false };
const todayMadrid = () => new Date().toLocaleDateString("es-ES",
    { timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric" });
const tid = (k) => `ac-${k.replace(/[._]/g, "-")}`;

const getVal = (doc, key) => key.split(".").reduce((o, k) => (o == null ? o : o[k]), doc);

// Contratos antiguos: días y horario a partir de la lista `schedule`.
const effective = (doc) => {
    const sched = (doc.schedule || []).filter(Boolean);
    return {
        ...doc,
        days: doc.days ?? [...new Set(sched.map((s) => s.day).filter(Boolean))],
        schedule_text: doc.schedule_text ?? sched.filter((s) => s.day || s.time)
            .map((s) => `${(s.day || "").charAt(0).toUpperCase()}${(s.day || "").slice(1)} ${s.time || ""}`.trim()).join("; "),
    };
};

function useWidth() {
    // Callback ref: el observador se engancha cuando el nodo se monta (aunque la plantilla llegue después).
    const [el, setEl] = useState(null);
    const [w, setW] = useState(0);
    useEffect(() => {
        if (!el) { return undefined; }
        const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, [el]);
    return [setEl, w];
}

function Mark({ f, on, readOnly, onToggle, s }) {
    return (
        <button type="button" disabled={readOnly} onClick={onToggle} data-testid={f.value ? `${tid(f.key)}-${f.value}` : tid(f.key)}
            aria-pressed={!!on} className="absolute flex items-center justify-center disabled:cursor-default"
            style={{ ...s, minHeight: 0, padding: 0, border: f.box ? "1px solid #000" : "none", background: readOnly ? "transparent" : "rgba(251,191,36,0.25)",
                color: INK, fontSize: s.height * 1.05, lineHeight: 1, fontWeight: 700 }}>
            {on ? "✕" : ""}
        </button>
    );
}

function Field({ f, doc, update, toggle, readOnly, scale }) {
    const [x0, y0, x1, y1] = f.rect;
    const s = { left: x0 * scale, top: y0 * scale, width: (x1 - x0) * scale, height: (y1 - y0) * scale };
    const val = getVal(doc, f.key);
    if (f.type === "check") return <Mark f={f} s={s} on={!!val} readOnly={readOnly} onToggle={() => toggle(f)} />;
    if (f.type === "radio") return <Mark f={f} s={s} on={val === f.value} readOnly={readOnly} onToggle={() => toggle(f)} />;
    if (f.type === "multi") return <Mark f={f} s={s} on={(val || []).includes(f.value)} readOnly={readOnly} onToggle={() => toggle(f)} />;
    if (f.type === "sign_date") {
        return <span className="absolute bg-white flex items-center" style={{ ...s, fontSize: 10 * scale, color: INK }} data-testid="ac-signed-date">{doc.signed_date || todayMadrid()}</span>;
    }
    if (f.type === "signature") {
        const name = doc[f.name_key];
        return (
            <div className="absolute flex flex-col items-center justify-end" style={s} data-testid={tid(f.key)}>
                {val ? <img src={val} alt="Firma" className="max-h-full max-w-full object-contain" />
                    : <span className="text-black/35 italic" style={{ fontSize: 8 * scale }}>Pendiente de firma</span>}
                {name && <span className="absolute whitespace-nowrap" style={{ top: (652 - y0) * scale, fontSize: 8 * scale, color: INK }}>{name}</span>}
            </div>
        );
    }
    return (
        <input type={f.type === "date" ? "date" : "text"} inputMode={f.type === "money" ? "decimal" : undefined}
            value={f.type === "money" && typeof val === "number" ? (val ? val.toFixed(2).replace(".", ",") : "") : (val ?? "")} readOnly={readOnly} onChange={(e) => update(f, e.target.value)} data-testid={tid(f.key)}
            className="absolute border-0 outline-none px-[2px] py-0 read-only:bg-transparent bg-amber-50/60 focus:bg-amber-100/80"
            style={{ ...s, minHeight: 0, fontSize: Math.min(9, (y1 - y0) * 0.75) * scale, color: INK, fontFamily: "Arial, Helvetica, sans-serif" }} />
    );
}

/** Contrato 2026: las páginas del PDF original con los campos para escribir encima (también en quiosco). */
export function AdultContractSheet({ doc, set, readOnly, layout }) {
    const [ref, width] = useWidth();
    if (!layout) return <div className="text-sm text-muted-foreground p-4">Cargando contrato…</div>;
    const [pw, ph] = layout.page_size;
    const scale = width / pw;
    const eff = effective(doc);
    const update = (f, v) => {
        if (f.key.startsWith("authorized_family.")) {
            const [, i, sub] = f.key.split(".");
            const arr = [...(doc.authorized_family || [])];
            while (arr.length < 5) arr.push({ ...EMPTY_MEMBER });
            arr[i] = { ...arr[i], [sub]: v };
            set({ authorized_family: arr });
            return;
        }
        set({ [f.key]: v });
    };
    const toggle = (f) => {
        const cur = getVal(eff, f.key);
        if (f.type === "radio") update(f, cur === f.value ? "" : f.value);
        else if (f.type === "multi") update(f, (cur || []).includes(f.value) ? cur.filter((x) => x !== f.value) : [...(cur || []), f.value]);
        else update(f, !cur);
    };
    return (
        <div ref={ref} className="mx-auto w-full max-w-[860px] min-w-[640px] space-y-3" data-testid="adult-contract-sheet">
            {width > 0 && layout.pages.map((src, pi) => (
                <div key={src} className="relative bg-white shadow-sm border border-black/10" style={{ width, height: ph * scale }} data-testid={`ac-page-${pi + 1}`}>
                    <img src={src} alt={`Contrato página ${pi + 1}`} className="absolute inset-0 w-full h-full select-none pointer-events-none" draggable={false} />
                    {doc.annex_title && pi === 0 && (
                        <span className="absolute text-center font-semibold" style={{ left: layout.annex_title_rect[0] * scale, top: layout.annex_title_rect[1] * scale,
                            width: (layout.annex_title_rect[2] - layout.annex_title_rect[0]) * scale, fontSize: 9.5 * scale, color: "#385623" }}>{doc.annex_title}</span>
                    )}
                    {layout.fields.filter((f) => f.page === pi).map((f) => (
                        <Field key={`${f.key}-${f.value || ""}`} f={f} doc={eff} update={update} toggle={toggle} readOnly={readOnly} scale={scale} />
                    ))}
                </div>
            ))}
        </div>
    );
}
